import { useState, useEffect, useMemo } from 'react';
import {
  archivalLegalHolds,
  archivalTransfers,
  archivalDispositions,
  type LegalHold,
  type LegalHoldStats,
  type ArchiveTransfer,
  type ArchiveTransferStats,
  type DispositionSchedule,
  type DispositionStats,
} from '@/services/archival';
import styles from './RecordsArchivalPage.module.css';

type TabType = 'dashboard' | 'legal-holds' | 'transfers' | 'dispositions';

export function RecordsArchivalPage() {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dashboard data
  const [legalHoldStats, setLegalHoldStats] = useState<LegalHoldStats | null>(null);
  const [transferStats, setTransferStats] = useState<ArchiveTransferStats | null>(null);
  const [dispositionStats, setDispositionStats] = useState<DispositionStats | null>(null);
  const [upcomingDispositions, setUpcomingDispositions] = useState<DispositionSchedule[]>([]);
  const [overdueDispositions, setOverdueDispositions] = useState<DispositionSchedule[]>([]);

  // List data
  const [legalHolds, setLegalHolds] = useState<LegalHold[]>([]);
  const [transfers, setTransfers] = useState<ArchiveTransfer[]>([]);
  const [dispositions, setDispositions] = useState<DispositionSchedule[]>([]);

  // Filters
  const [holdFilter, setHoldFilter] = useState<string>('all');
  const [transferFilter, setTransferFilter] = useState<string>('all');
  const [dispositionFilter, setDispositionFilter] = useState<string>('all');

  // Modals
  const [selectedHold, setSelectedHold] = useState<LegalHold | null>(null);
  const [selectedTransfer, setSelectedTransfer] = useState<ArchiveTransfer | null>(null);
  const [selectedDisposition, setSelectedDisposition] = useState<DispositionSchedule | null>(null);
  const [showCreateHoldModal, setShowCreateHoldModal] = useState(false);
  const [showCreateTransferModal, setShowCreateTransferModal] = useState(false);

  // Load dashboard data
  useEffect(() => {
    async function loadDashboard() {
      setLoading(true);
      try {
        const [holdsRes, transfersRes, dispositionsRes, upcomingRes, overdueRes] = await Promise.all([
          archivalLegalHolds.getStats(),
          archivalTransfers.getStats(),
          archivalDispositions.getStats(),
          archivalDispositions.getUpcoming(90),
          archivalDispositions.getOverdue(),
        ]);

        if (holdsRes.success && holdsRes.data) setLegalHoldStats(holdsRes.data);
        if (transfersRes.success && transfersRes.data) setTransferStats(transfersRes.data);
        if (dispositionsRes.success && dispositionsRes.data) setDispositionStats(dispositionsRes.data);
        if (upcomingRes.success && upcomingRes.data) setUpcomingDispositions(upcomingRes.data);
        if (overdueRes.success && overdueRes.data) setOverdueDispositions(overdueRes.data);
      } catch {
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    }
    loadDashboard();
  }, []);

  // Load tab-specific data
  useEffect(() => {
    async function loadTabData() {
      if (activeTab === 'legal-holds') {
        const response = await archivalLegalHolds.list({ perPage: 50 });
        if (response.success && response.data) setLegalHolds(response.data);
      } else if (activeTab === 'transfers') {
        const response = await archivalTransfers.list({ perPage: 50 });
        if (response.success && response.data) setTransfers(response.data);
      } else if (activeTab === 'dispositions') {
        const response = await archivalDispositions.list({ perPage: 50 });
        if (response.success && response.data) setDispositions(response.data);
      }
    }
    loadTabData();
  }, [activeTab]);

  // Filtered data
  const filteredHolds = useMemo(() => {
    if (holdFilter === 'all') return legalHolds;
    return legalHolds.filter(h => h.status === holdFilter);
  }, [legalHolds, holdFilter]);

  const filteredTransfers = useMemo(() => {
    if (transferFilter === 'all') return transfers;
    return transfers.filter(t => t.status === transferFilter);
  }, [transfers, transferFilter]);

  const filteredDispositions = useMemo(() => {
    if (dispositionFilter === 'all') return dispositions;
    return dispositions.filter(d => d.status === dispositionFilter);
  }, [dispositions, dispositionFilter]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusClass = (status: string) => {
    const classes: Record<string, string> = {
      active: styles.statusActive,
      released: styles.statusReleased,
      expired: styles.statusExpired,
      draft: styles.statusDraft,
      submitted: styles.statusSubmitted,
      approved: styles.statusApproved,
      rejected: styles.statusRejected,
      in_transit: styles.statusInTransit,
      completed: styles.statusCompleted,
      cancelled: styles.statusCancelled,
      scheduled: styles.statusScheduled,
      executed: styles.statusExecuted,
      deferred: styles.statusDeferred,
    };
    return classes[status] || '';
  };

  const getDispositionActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      archive_praad: 'Archive to PRAAD',
      archive_mda: 'MDA Archive',
      destroy: 'Destroy',
      review: 'Review Required',
      permanent_retention: 'Permanent Retention',
    };
    return labels[action] || action;
  };

  const getTransferTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      praad: 'PRAAD Transfer',
      mda_archive: 'MDA Archive',
      destruction: 'Destruction',
    };
    return labels[type] || type;
  };

  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'dashboard', label: 'Overview', icon: '📊' },
    { id: 'legal-holds', label: 'Legal Holds', icon: '⚖️' },
    { id: 'transfers', label: 'Archive Transfers', icon: '📦' },
    { id: 'dispositions', label: 'Disposition Schedule', icon: '📅' },
  ];

  if (loading && activeTab === 'dashboard') {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading archival data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Records Archival</h1>
          <p className={styles.subtitle}>
            Legal holds, archive transfers, and disposition management
          </p>
        </div>
        <div className={styles.headerActions}>
          {activeTab === 'legal-holds' && (
            <button className={styles.primaryBtn} onClick={() => setShowCreateHoldModal(true)}>
              <span className={styles.btnIcon}>+</span>
              Create Legal Hold
            </button>
          )}
          {activeTab === 'transfers' && (
            <button className={styles.primaryBtn} onClick={() => setShowCreateTransferModal(true)}>
              <span className={styles.btnIcon}>+</span>
              New Transfer
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className={styles.errorBanner}>
          <span>Error: {error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className={styles.dashboardTab}>
            {/* Stats Grid */}
            <div className={styles.statsGrid}>
              <div className={`${styles.statCard} ${styles.statHolds}`}>
                <div className={styles.statIcon}>⚖️</div>
                <div className={styles.statInfo}>
                  <div className={styles.statValue}>{legalHoldStats?.active || 0}</div>
                  <div className={styles.statLabel}>Active Legal Holds</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.statTransfers}`}>
                <div className={styles.statIcon}>📦</div>
                <div className={styles.statInfo}>
                  <div className={styles.statValue}>{(transferStats?.submitted || 0) + (transferStats?.approved || 0)}</div>
                  <div className={styles.statLabel}>Pending Transfers</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.statUpcoming}`}>
                <div className={styles.statIcon}>📆</div>
                <div className={styles.statInfo}>
                  <div className={styles.statValue}>{dispositionStats?.upcomingThisMonth || 0}</div>
                  <div className={styles.statLabel}>Dispositions This Month</div>
                </div>
              </div>
              <div className={`${styles.statCard} ${styles.statOverdue}`}>
                <div className={styles.statIcon}>⚠️</div>
                <div className={styles.statInfo}>
                  <div className={styles.statValue}>{dispositionStats?.overdueCount || 0}</div>
                  <div className={styles.statLabel}>Overdue Dispositions</div>
                </div>
              </div>
            </div>

            {/* Alert Panels */}
            <div className={styles.alertPanels}>
              {/* Overdue Dispositions */}
              <div className={styles.alertPanel}>
                <div className={styles.alertHeader}>
                  <h3 className={styles.alertTitle}>
                    <span className={styles.alertIconRed}>⚠️</span>
                    Overdue Dispositions ({overdueDispositions.length})
                  </h3>
                </div>
                <div className={styles.alertList}>
                  {overdueDispositions.length === 0 ? (
                    <p className={styles.noAlerts}>No overdue dispositions</p>
                  ) : (
                    overdueDispositions.slice(0, 5).map(disp => (
                      <div key={disp.id} className={styles.alertItem} onClick={() => setSelectedDisposition(disp)}>
                        <div className={styles.alertInfo}>
                          <span className={styles.alertName}>
                            {disp.pfile?.staff.firstName} {disp.pfile?.staff.lastName}
                          </span>
                          <span className={styles.alertFile}>{disp.pfile?.fileNumber || disp.document?.title}</span>
                        </div>
                        <span className={styles.alertDueDate}>
                          Due: {formatDate(disp.scheduledDate)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Upcoming Dispositions */}
              <div className={styles.alertPanel}>
                <div className={styles.alertHeader}>
                  <h3 className={styles.alertTitle}>
                    <span className={styles.alertIconYellow}>📅</span>
                    Upcoming Dispositions ({upcomingDispositions.length})
                  </h3>
                </div>
                <div className={styles.alertList}>
                  {upcomingDispositions.length === 0 ? (
                    <p className={styles.noAlerts}>No upcoming dispositions</p>
                  ) : (
                    upcomingDispositions.slice(0, 5).map(disp => (
                      <div key={disp.id} className={styles.alertItem} onClick={() => setSelectedDisposition(disp)}>
                        <div className={styles.alertInfo}>
                          <span className={styles.alertName}>
                            {disp.pfile?.staff.firstName} {disp.pfile?.staff.lastName}
                          </span>
                          <span className={styles.alertFile}>{disp.pfile?.fileNumber || disp.document?.title}</span>
                        </div>
                        <span className={`${styles.actionBadge} ${styles[`action${disp.dispositionAction.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`]}`}>
                          {getDispositionActionLabel(disp.dispositionAction)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Breakdown Sections */}
            <div className={styles.breakdownRow}>
              {/* Transfer Status Breakdown */}
              <div className={styles.breakdownSection}>
                <h3 className={styles.breakdownTitle}>Archive Transfers</h3>
                <div className={styles.breakdownGrid}>
                  <div className={styles.breakdownItem}>
                    <span className={styles.breakdownCount}>{transferStats?.draft || 0}</span>
                    <span className={styles.breakdownLabel}>Draft</span>
                  </div>
                  <div className={styles.breakdownItem}>
                    <span className={styles.breakdownCount}>{transferStats?.submitted || 0}</span>
                    <span className={styles.breakdownLabel}>Submitted</span>
                  </div>
                  <div className={styles.breakdownItem}>
                    <span className={styles.breakdownCount}>{transferStats?.approved || 0}</span>
                    <span className={styles.breakdownLabel}>Approved</span>
                  </div>
                  <div className={styles.breakdownItem}>
                    <span className={styles.breakdownCount}>{transferStats?.inTransit || 0}</span>
                    <span className={styles.breakdownLabel}>In Transit</span>
                  </div>
                  <div className={styles.breakdownItem}>
                    <span className={styles.breakdownCount}>{transferStats?.completed || 0}</span>
                    <span className={styles.breakdownLabel}>Completed</span>
                  </div>
                </div>
              </div>

              {/* Disposition Actions */}
              <div className={styles.breakdownSection}>
                <h3 className={styles.breakdownTitle}>Disposition by Action</h3>
                <div className={styles.breakdownGrid}>
                  {dispositionStats?.byAction.map(item => (
                    <div key={item.action} className={styles.breakdownItem}>
                      <span className={styles.breakdownCount}>{item.count}</span>
                      <span className={styles.breakdownLabel}>{getDispositionActionLabel(item.action)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Legal Holds Tab */}
        {activeTab === 'legal-holds' && (
          <div className={styles.listTab}>
            <div className={styles.filterBar}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Status</label>
                <select
                  className={styles.filterSelect}
                  value={holdFilter}
                  onChange={e => setHoldFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="released">Released</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <span className={styles.filterCount}>
                Showing {filteredHolds.length} of {legalHolds.length} holds
              </span>
            </div>

            <div className={styles.cardGrid}>
              {filteredHolds.map(hold => (
                <div
                  key={hold.id}
                  className={`${styles.holdCard} ${hold.status === 'active' ? styles.holdActive : ''}`}
                  onClick={() => setSelectedHold(hold)}
                >
                  <div className={styles.holdHeader}>
                    <h4 className={styles.holdName}>{hold.holdName}</h4>
                    <span className={`${styles.statusBadge} ${getStatusClass(hold.status)}`}>
                      {hold.status}
                    </span>
                  </div>
                  <p className={styles.holdMatter}>{hold.legalMatter}</p>
                  {hold.caseReference && (
                    <div className={styles.holdRef}>
                      <span className={styles.refIcon}>📋</span>
                      {hold.caseReference}
                    </div>
                  )}
                  <div className={styles.holdMeta}>
                    <span className={styles.holdScope}>{hold.scope.replace('_', ' ')}</span>
                    <span className={styles.holdDate}>Since {formatDate(hold.startDate)}</span>
                  </div>
                  {hold.pfile && (
                    <div className={styles.holdTarget}>
                      <span className={styles.targetIcon}>📁</span>
                      {hold.pfile.staff.firstName} {hold.pfile.staff.lastName} - {hold.pfile.fileNumber}
                    </div>
                  )}
                  {hold.document && (
                    <div className={styles.holdTarget}>
                      <span className={styles.targetIcon}>📄</span>
                      {hold.document.title}
                    </div>
                  )}
                </div>
              ))}
              {filteredHolds.length === 0 && (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>⚖️</span>
                  <p>No legal holds found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Archive Transfers Tab */}
        {activeTab === 'transfers' && (
          <div className={styles.listTab}>
            <div className={styles.filterBar}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Status</label>
                <select
                  className={styles.filterSelect}
                  value={transferFilter}
                  onChange={e => setTransferFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="in_transit">In Transit</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <span className={styles.filterCount}>
                Showing {filteredTransfers.length} of {transfers.length} transfers
              </span>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Transfer #</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th>Verified</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransfers.map(transfer => (
                    <tr key={transfer.id}>
                      <td className={styles.transferNumber}>{transfer.transferNumber}</td>
                      <td>
                        <span className={`${styles.typeBadge} ${styles[`type${transfer.transferType.charAt(0).toUpperCase() + transfer.transferType.slice(1)}`]}`}>
                          {getTransferTypeLabel(transfer.transferType)}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${getStatusClass(transfer.status)}`}>
                          {transfer.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className={styles.countCell}>{transfer.totalItems}</td>
                      <td className={styles.countCell}>
                        {transfer.verifiedItems}/{transfer.totalItems}
                      </td>
                      <td>{formatDate(transfer.createdAt)}</td>
                      <td>
                        <button
                          className={styles.viewBtn}
                          onClick={() => setSelectedTransfer(transfer)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredTransfers.length === 0 && (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>📦</span>
                  <p>No archive transfers found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Disposition Schedule Tab */}
        {activeTab === 'dispositions' && (
          <div className={styles.listTab}>
            <div className={styles.filterBar}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Status</label>
                <select
                  className={styles.filterSelect}
                  value={dispositionFilter}
                  onChange={e => setDispositionFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="approved">Approved</option>
                  <option value="executed">Executed</option>
                  <option value="deferred">Deferred</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <span className={styles.filterCount}>
                Showing {filteredDispositions.length} of {dispositions.length} schedules
              </span>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Record</th>
                    <th>Action</th>
                    <th>Scheduled Date</th>
                    <th>Status</th>
                    <th>On Hold</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDispositions.map(disp => (
                    <tr key={disp.id} className={disp.isOnHold ? styles.onHoldRow : ''}>
                      <td>
                        <div className={styles.recordInfo}>
                          <span className={styles.recordName}>
                            {disp.pfile?.staff.firstName} {disp.pfile?.staff.lastName}
                          </span>
                          <span className={styles.recordFile}>
                            {disp.pfile?.fileNumber || disp.document?.title}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.actionBadge} ${styles[`action${disp.dispositionAction.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`]}`}>
                          {getDispositionActionLabel(disp.dispositionAction)}
                        </span>
                      </td>
                      <td>{formatDate(disp.scheduledDate)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${getStatusClass(disp.status)}`}>
                          {disp.status}
                        </span>
                      </td>
                      <td>
                        {disp.isOnHold ? (
                          <span className={styles.holdBadge}>⚖️ Yes</span>
                        ) : (
                          <span className={styles.noHoldBadge}>—</span>
                        )}
                      </td>
                      <td>
                        <button
                          className={styles.viewBtn}
                          onClick={() => setSelectedDisposition(disp)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredDispositions.length === 0 && (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>📅</span>
                  <p>No disposition schedules found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Legal Hold Detail Modal */}
      {selectedHold && (
        <div className={styles.modalOverlay} onClick={() => setSelectedHold(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>{selectedHold.holdName}</h2>
                <p className={styles.modalSubtitle}>
                  {selectedHold.caseReference && `Case: ${selectedHold.caseReference}`}
                </p>
              </div>
              <button className={styles.modalClose} onClick={() => setSelectedHold(null)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailSection}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Status</span>
                  <span className={`${styles.statusBadge} ${getStatusClass(selectedHold.status)}`}>
                    {selectedHold.status}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Scope</span>
                  <span>{selectedHold.scope.replace('_', ' ')}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Start Date</span>
                  <span>{formatDate(selectedHold.startDate)}</span>
                </div>
                {selectedHold.endDate && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>End Date</span>
                    <span>{formatDate(selectedHold.endDate)}</span>
                  </div>
                )}
              </div>
              <div className={styles.detailSection}>
                <h4>Legal Matter</h4>
                <p className={styles.detailText}>{selectedHold.legalMatter}</p>
              </div>
              {selectedHold.notes && (
                <div className={styles.detailSection}>
                  <h4>Notes</h4>
                  <p className={styles.detailText}>{selectedHold.notes}</p>
                </div>
              )}
              {selectedHold.status === 'active' && (
                <div className={styles.modalActions}>
                  <button className={styles.releaseBtn}>
                    Release Hold
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transfer Detail Modal */}
      {selectedTransfer && (
        <div className={styles.modalOverlay} onClick={() => setSelectedTransfer(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>Transfer {selectedTransfer.transferNumber}</h2>
                <p className={styles.modalSubtitle}>
                  {getTransferTypeLabel(selectedTransfer.transferType)}
                </p>
              </div>
              <button className={styles.modalClose} onClick={() => setSelectedTransfer(null)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailSection}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Status</span>
                  <span className={`${styles.statusBadge} ${getStatusClass(selectedTransfer.status)}`}>
                    {selectedTransfer.status.replace('_', ' ')}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Items</span>
                  <span>{selectedTransfer.totalItems} total ({selectedTransfer.verifiedItems} verified)</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Created</span>
                  <span>{formatDate(selectedTransfer.createdAt)}</span>
                </div>
                {selectedTransfer.praadReference && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>PRAAD Reference</span>
                    <span>{selectedTransfer.praadReference}</span>
                  </div>
                )}
                {selectedTransfer.accessionNumber && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Accession Number</span>
                    <span>{selectedTransfer.accessionNumber}</span>
                  </div>
                )}
              </div>
              {selectedTransfer.notes && (
                <div className={styles.detailSection}>
                  <h4>Notes</h4>
                  <p className={styles.detailText}>{selectedTransfer.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Disposition Detail Modal */}
      {selectedDisposition && (
        <div className={styles.modalOverlay} onClick={() => setSelectedDisposition(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>Disposition Schedule</h2>
                <p className={styles.modalSubtitle}>
                  {selectedDisposition.pfile?.staff.firstName} {selectedDisposition.pfile?.staff.lastName}
                </p>
              </div>
              <button className={styles.modalClose} onClick={() => setSelectedDisposition(null)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailSection}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>File Number</span>
                  <span className={styles.monoText}>{selectedDisposition.pfile?.fileNumber}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Action</span>
                  <span className={`${styles.actionBadge} ${styles[`action${selectedDisposition.dispositionAction.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`]}`}>
                    {getDispositionActionLabel(selectedDisposition.dispositionAction)}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Scheduled Date</span>
                  <span>{formatDate(selectedDisposition.scheduledDate)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Status</span>
                  <span className={`${styles.statusBadge} ${getStatusClass(selectedDisposition.status)}`}>
                    {selectedDisposition.status}
                  </span>
                </div>
              </div>

              {selectedDisposition.isOnHold && (
                <div className={styles.holdAlert}>
                  <span className={styles.holdAlertIcon}>⚖️</span>
                  <div>
                    <strong>On Legal Hold</strong>
                    <p>This disposition is blocked by an active legal hold.</p>
                  </div>
                </div>
              )}

              {selectedDisposition.retentionRuleName && (
                <div className={styles.detailSection}>
                  <h4>Retention Information</h4>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Rule</span>
                    <span>{selectedDisposition.retentionRuleName}</span>
                  </div>
                  {selectedDisposition.retentionPeriodYears && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Period</span>
                      <span>{selectedDisposition.retentionPeriodYears} years</span>
                    </div>
                  )}
                </div>
              )}

              {selectedDisposition.status === 'scheduled' && !selectedDisposition.isOnHold && (
                <div className={styles.modalActions}>
                  <button className={styles.approveBtn}>Approve</button>
                  <button className={styles.deferBtn}>Defer</button>
                  <button className={styles.cancelBtn}>Cancel</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Legal Hold Modal (placeholder) */}
      {showCreateHoldModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCreateHoldModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Create Legal Hold</h2>
              <button className={styles.modalClose} onClick={() => setShowCreateHoldModal(false)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.placeholderText}>Legal hold creation form will be implemented here.</p>
            </div>
          </div>
        </div>
      )}

      {/* Create Transfer Modal (placeholder) */}
      {showCreateTransferModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCreateTransferModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>New Archive Transfer</h2>
              <button className={styles.modalClose} onClick={() => setShowCreateTransferModal(false)}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.placeholderText}>Transfer creation wizard will be implemented here.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
